import { useMemo, useState } from 'react';
import { useAppSelector } from '@store/hooks';
import { useNow } from '@hooks/useNow';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useWorkingHours } from '@hooks/useWorkingHours';
import { DateTime } from 'luxon';
import type { TFunction } from 'i18next';
import {
  selectLiveSummary,
  selectStaticTags,
  selectWorkforceRows,
} from '@store/selectors/monitor.selectors';
import { tradeLabel } from '@utils/tradeLabel';
import { ClockIcon, ProhibitedIcon, ShieldIcon, UsersIcon } from '@utils/icons';
import { deriveShiftState, type ShiftState } from '@utils/shiftSchedule';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { DashboardCard } from './_shared/DashboardCard';
import styles from './workforceStatusCard.module.css';

/** Build the shift-footer label from a derived state. The "next change"
 *  countdown is real (computed from the live clock against the site's schedule)
 *  — the previous hardcoded "Next change in 4h" never moved. */
function shiftLabel(shift: ShiftState, t: TFunction): string {
  const base = 'dashboard.workforce.shift';
  switch (shift.status) {
    case 'before':
      return t(`${base}.before`, {
        range: shift.range,
        duration: formatCountdown(shift.minutesToChange, t),
      });
    case 'active':
      return shift.minutesToChange === null
        ? t(`${base}.range`, { range: shift.range })
        : t(`${base}.active`, {
            range: shift.range,
            duration: formatCountdown(shift.minutesToChange, t),
          });
    case 'after':
      return t(`${base}.after`, { range: shift.range });
    case 'off':
      return t(`${base}.off`);
    case 'unavailable':
      return t(`${base}.unavailable`);
  }
}

/** Localised "Xh Ym" / "Xh" / "Ym" for a positive minute count. Named to avoid
 *  shadowing the unrelated `formatDuration` (HH:MM) export in dateUtils. */
function formatCountdown(minutes: number, t: TFunction): string {
  const base = 'dashboard.workforce.shift.duration';
  const hours = Math.floor(minutes / 60);
  const remainderMinutes = minutes % 60;
  if (hours > 0 && remainderMinutes > 0) {
    return t(`${base}.hm`, { h: hours, m: remainderMinutes });
  }
  if (hours > 0) return t(`${base}.h`, { h: hours });
  return t(`${base}.m`, { m: remainderMinutes });
}

/* ----------------------------------------------------------------
   Workforce Status — mirrors the Lovable command-center widget.

   All numbers are derived from real Trusstor data:
     ON-SITE total       = selectLiveSummary.workersOnSite
                           (sum of activeTagsIds across professions)
     MISSING             = staticTags.length − workersOnSite
                           (full project roster minus currently present)
     CERTIFIED %         = staticTags where every cert.expirationDate
                           is in the future, divided by total assigned
     Trade name          = tradeLabel(selectWorkforceRows.tradeId), with
                           tradeGroup fallback (matches the Visual Site
                           Overview chip label; specialty is not used)
     Trade present       = selectWorkforceRows[*].active
                           (active tag count for that profession)
     Trade total         = staticTags filtered by professionId

   Shift footer       = the linked site's `workDayHours` for today (start/end
                        window in the site tz), with a live countdown to the
                        next change. Neutral "unavailable" state when the site
                        isn't linked to a project that carries a schedule.
---------------------------------------------------------------- */


/** A tag is certified when it carries no cert that has already expired.
    Tags with no certificates are treated as certified (i.e. no required
    cert) — matches Trusstor's `certificateExpired` safety violation logic,
    which only fires when an explicit certificate has lapsed. */
function isTagCertified(certs: { expirationDate?: string }[], nowMs: number): boolean {
  for (const c of certs) {
    if (!c.expirationDate) continue;
    const exp = Date.parse(c.expirationDate);
    if (Number.isFinite(exp) && exp < nowMs) return false;
  }
  return true;
}

export function WorkforceStatusCard() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const summary = useAppSelector(selectLiveSummary);
  const rows = useAppSelector(selectWorkforceRows);
  const staticTags = useAppSelector(selectStaticTags);

  /* Shift schedule comes from the linked Trusstor project's working hours.
     Schedule changes rarely, so cache it for a few minutes. */
  const { data: workingHours, isLoading: shiftLoading } = useWorkingHours(siteId);

  /* Tick the clock once a minute so the "next change" countdown stays live
     without refetching. */
  const now = DateTime.fromJSDate(useNow(60_000));
  const shift = shiftLoading
    ? '—'
    : shiftLabel(deriveShiftState(workingHours, now), t);

  /* Derive total-assigned by profession + the headline missing / certified
     counts in one pass over staticTags. `nowMs` captured once at mount via
     `useState`'s lazy initialiser to satisfy `react-hooks/purity`. */
  const [nowMs] = useState(() => Date.now());
  const { assignedByProfession, totalAssigned, certifiedPct } = useMemo(() => {
    const byProf = new Map<string, number>();
    let certified = 0;
    for (const tag of staticTags) {
      if (tag.professionId) {
        byProf.set(tag.professionId, (byProf.get(tag.professionId) ?? 0) + 1);
      }
      if (isTagCertified(tag.certificates ?? [], nowMs)) certified += 1;
    }
    const total = staticTags.length;
    const pct = total === 0 ? 100 : Math.round((certified / total) * 100);
    return { assignedByProfession: byProf, totalAssigned: total, certifiedPct: pct };
  }, [staticTags, nowMs]);

  const missing = Math.max(0, totalAssigned - summary.workersOnSite);

  // Every profession currently on site, ranked by present count.
  // Intentionally not truncated — real sites carry many more
  // professions than Lovable's 4-row fixture and the list scrolls.
  const sortedRows = useMemo(
    () => [...rows].filter((row) => row.active > 0).sort((rowA, rowB) => rowB.active - rowA.active),
    [rows],
  );

  return (
    <DashboardCard
      title={t('dashboard.workforce.title')}
      bodyClassName={styles.body}
    >
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statIcon}><UsersIcon size={18} /></span>
          <span className={styles.statValue}>{summary.workersOnSite}</span>
          <span className={styles.statLabel}>{t('dashboard.workforce.onSite')}</span>
        </div>
        <div className={`${styles.stat} ${styles.statMissing}`}>
          <span className={styles.statIcon}><ProhibitedIcon size={18} /></span>
          <span className={styles.statValue}>{missing}</span>
          <span className={styles.statLabel}>{t('dashboard.workforce.missing')}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statIcon}><ShieldIcon size={18} /></span>
          <span className={styles.statValue}>{certifiedPct}%</span>
          <span className={styles.statLabel}>{t('dashboard.workforce.certified')}</span>
        </div>
      </div>

      <ScrollArea className={styles.tradeScroll}>
        <ul className={styles.tradeList}>
          {sortedRows.map((row) => {
            // Label by trade (not specialty) so a profession reads the same as its
            // Visual Site Overview chip, which uses tradeLabel(tradeId). Specialty
            // is intentionally ignored here — in the upstream feed it carries
            // free-text sub-trade values ("Earthquake", "Ceramics") that don't
            // match the building-view labels.
            const label = (row.tradeId ? tradeLabel(t, row.tradeId) : row.tradeGroup) ?? '—';
            /* Assigned comes from the static roster; fall back to the live
               count if the roster hasn't hydrated for this profession yet. */
            const assigned = assignedByProfession.get(row.id) ?? row.active;
            const ratio = assigned > 0 ? Math.min(1, row.active / assigned) : 0;
            /* Colour each bar by the profession's own backgroundColor (the same
               field the Visual Site Overview chips use, via selectWorkforceRows),
               so a profession reads as the same colour across both cards. */
            const barColor = row.tradeColor;
            return (
              <li key={row.id} className={styles.tradeRow}>
                <span className={styles.tradeName} title={label}>{label}</span>
                <div className={styles.tradeBar}>
                  <div
                    className={styles.tradeBarFill}
                    style={{ width: `${ratio * 100}%`, background: barColor }}
                  />
                </div>
                <span className={styles.tradeRatio}>
                  {row.active}/{assigned}
                </span>
              </li>
            );
          })}
        </ul>
      </ScrollArea>

      <div className={styles.shift}>
        <span className={styles.shiftIcon}><ClockIcon size={14} /></span>
        {shift}
      </div>
    </DashboardCard>
  );
}
